import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getRichProduct } from "@/lib/products";
import { useGangSheetBuilderScript } from "@/hooks/useGangSheetBuilderScript";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Zap,
  Upload,
  ChevronDown,
  ChevronUp,
  ShoppingBag,
  AlertTriangle,
  ShoppingCart,
  Minus,
  Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useCart } from "@/lib/cart";
import { toast } from "sonner";

export const Route = createFileRoute("/app/customer/shop/$productId")({ component: ProductDetail });

function ProductDetail() {
  const { productId } = Route.useParams();
  const nav = useNavigate();
  const {
    shopId,
    openBuilder,
    isEnabled: dripEnabled,
    status: builderStatus,
    error: builderError,
  } = useGangSheetBuilderScript();
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState(0);
  const [metres, setMetres] = useState<number>(1);
  const [quantity, setQuantity] = useState<number>(1);
  const [selectedVariantIndex, setSelectedVariantIndex] = useState<number>(0);
  const { addItem, openCart } = useCart();

  const { data: product, isLoading } = useQuery({
    queryKey: ["rich-product", productId],
    queryFn: () => getRichProduct(productId),
  });

  if (isLoading) return <div className="h-96 rounded-3xl bg-muted animate-pulse" />;
  if (!product)
    return (
      <div className="rounded-3xl border border-border bg-card p-12 text-center shadow-soft">
        <ShoppingBag className="mx-auto mb-4 h-10 w-10 text-muted-foreground/40" />
        <p className="text-muted-foreground">Product not found.</p>
      </div>
    );

  const allImages = [
    ...(product.image_main_url ? [product.image_main_url] : []),
    ...product.image_gallery_urls,
  ];
  const activeImage = allImages[activeGalleryIndex] ?? null;

  const variantList = (product.variants ?? []) as Array<{
    label?: string;
    width_mm?: number;
    height_mm?: number;
    price_modifier?: number;
  }>;
  const selectedVariant = variantList[selectedVariantIndex] ?? null;

  const gangsheetMissingConfig = product.is_gangsheet && (!dripEnabled || !shopId);

  const handleStartGangSheet = async () => {
    if (!product.is_gangsheet) return;
    if (builderStatus === "checking" || builderStatus === "loading") {
      toast.info("Gang sheet builder is still loading — try again in a moment.");
      return;
    }
    if (builderStatus !== "ready") {
      toast.error(
        builderError ??
          "Gang sheet builder isn't available right now. Check Settings → Integrations.",
      );
      return;
    }
    try {
      await openBuilder({
        product_code: product.code,
        width_mm: selectedVariant?.width_mm,
        height_mm: selectedVariant?.height_mm,
        onSaved: (designId, info) => {
          // Builder finished — add the configured product to the cart with
          // the saved gang sheet design attached, then send the user to
          // checkout via the cart.
          const variant = selectedVariant;
          const basePrice = parseFloat(String(product.price_per_m || "0")) || 0;
          const modifier = variant?.price_modifier ?? 0;
          addItem({
            productId: product.id,
            productName: product.name,
            productCode: product.code,
            productImage: product.image_main_url ?? null,
            pricePerMetre: String(basePrice + modifier),
            basePricePerMetre: String(basePrice),
            priceModifier: modifier || undefined,
            metres,
            quantity,
            variantLabel: variant?.label,
            widthMm: variant?.width_mm,
            heightMm: variant?.height_mm,
            requiresGangSheet: true,
            allowManualUpload: !!product.allow_manual_upload,
            gangSheetLayoutId: designId,
            gangSheetPreviewUrl: info?.previewUrl ?? null,
            gangSheetSavedAt: info?.savedAt ?? new Date().toISOString(),
            gangSheetName: info?.designName ?? null,
            taxUseGlobal: product.tax_use_global ?? true,
            taxRateOverride: product.tax_rate_override ?? null,
          });
          toast.success("Gang sheet saved and added to cart");
          nav({ to: "/app/customer/cart" });
        },
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not open the gang sheet builder.");
    }
  };

  const handleStartJob = () => {
    nav({ to: "/app/customer/new-job", search: { product: product.name } });
  };

  const handleAddToCart = () => {
    if (metres < 1) {
      toast.error("Minimum 1 metre");
      return;
    }
    const variant = selectedVariant;
    const basePrice = parseFloat(String(product.price_per_m || "0")) || 0;
    const modifier = variant?.price_modifier ?? 0;
    addItem({
      productId: product.id,
      productName: product.name,
      productCode: product.code,
      productImage: product.image_main_url ?? null,
      pricePerMetre: String(basePrice + modifier),
      basePricePerMetre: String(basePrice),
      priceModifier: modifier || undefined,
      metres,
      quantity,
      variantLabel: variant?.label,
      widthMm: variant?.width_mm,
      heightMm: variant?.height_mm,
      requiresGangSheet: !!product.is_gangsheet,
      allowManualUpload: !!product.allow_manual_upload,
      taxUseGlobal: product.tax_use_global ?? true,
      taxRateOverride: product.tax_rate_override ?? null,
    });
    toast.success(`Added to cart`);
    openCart();
  };

  const basePricePerM = parseFloat(String(product.price_per_m || "0")) || 0;
  const pricePerM = basePricePerM + (selectedVariant?.price_modifier ?? 0);
  const lineTotal = pricePerM * metres * quantity;

  return (
    <div className="space-y-6 pb-12">
      {/* breadcrumb */}
      <Link
        to="/app/customer/shop"
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="h-4 w-4" /> Back to shop
      </Link>

      {/* hero header */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-[2rem] bg-gradient-hero p-8 text-primary-foreground shadow-pop"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/10 blur-2xl"
        />
        {product.ribbon_enabled && product.ribbon_text && (
          <span className="absolute right-6 top-6 rounded-full bg-card px-3 py-1 text-xs font-semibold text-foreground shadow-soft">
            {product.ribbon_text}
          </span>
        )}
        <div className="relative">
          <h1 className="text-4xl font-bold tracking-tight md:text-5xl">{product.name}</h1>
          {product.short_description && (
            <p className="mt-2 text-lg text-primary-foreground/90">{product.short_description}</p>
          )}
          <p className="mt-3 text-sm text-primary-foreground/80 font-medium">
            From £{parseFloat(String(product.price_per_m || "0")).toFixed(2)} per metre
          </p>
        </div>
      </motion.div>

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        {/* left column */}
        <div className="space-y-6">
          {/* images */}
          {allImages.length > 0 && (
            <div className="space-y-3">
              <div className="aspect-[4/3] overflow-hidden rounded-3xl border border-border bg-muted shadow-soft">
                <img src={activeImage!} alt={product.name} className="h-full w-full object-cover" />
              </div>
              {allImages.length > 1 && (
                <div className="flex gap-2 flex-wrap">
                  {allImages.map((url, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveGalleryIndex(i)}
                      className={cn(
                        "h-16 w-16 rounded-xl overflow-hidden border-2 transition-all",
                        i === activeGalleryIndex ? "border-primary shadow-soft" : "border-border",
                      )}
                    >
                      <img src={url} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* full description */}
          {product.full_description && (
            <div className="rounded-3xl border border-border bg-card p-6 shadow-soft">
              <h2 className="text-base font-semibold mb-3">About this product</h2>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {product.full_description}
              </p>
            </div>
          )}

          {/* additional sections as accordion */}
          {product.additional_sections.length > 0 && (
            <div className="rounded-3xl border border-border bg-card shadow-soft overflow-hidden">
              {product.additional_sections.map((sec, i) => (
                <div key={i} className="border-b border-border last:border-0">
                  <button
                    className="flex w-full items-center justify-between px-6 py-4 text-sm font-medium hover:bg-muted/30 transition-colors"
                    onClick={() => setActiveTab(activeTab === sec.title ? null : sec.title)}
                  >
                    {sec.title}
                    {activeTab === sec.title ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                  {activeTab === sec.title && (
                    <div className="px-6 pb-5 text-sm text-muted-foreground whitespace-pre-wrap">
                      {sec.description}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* right column – buy panel */}
        <div className="space-y-4">
          <div className="rounded-3xl border border-border bg-card p-6 shadow-soft space-y-5 sticky top-6">
            <div className="flex items-baseline justify-between">
              <div className="text-2xl font-bold">
                £{pricePerM.toFixed(2)}
                <span className="text-sm font-normal text-muted-foreground">/m</span>
              </div>
              {product.is_gangsheet && (
                <span className="text-[11px] font-semibold uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                  Gang sheet
                </span>
              )}
            </div>

            {/* Visible option selectors */}
            <div className="space-y-4">
              {variantList.length > 0 && (
                <div>
                  <Label className="text-xs font-medium">Options</Label>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {variantList.map((v, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedVariantIndex(i)}
                        className={cn(
                          "rounded-xl border px-3 py-2 text-xs font-medium transition-all",
                          selectedVariantIndex === i
                            ? "border-primary bg-primary/10 text-primary"
                            : "border-border hover:border-primary/40",
                        )}
                      >
                        <span className="block">
                          {v.label ||
                            (v.width_mm && v.height_mm
                              ? `${v.width_mm}×${v.height_mm}mm`
                              : `Option ${i + 1}`)}
                        </span>
                        {v.label && v.width_mm && v.height_mm && (
                          <span className="block text-[10px] opacity-70">
                            {v.width_mm}×{v.height_mm}mm
                          </span>
                        )}
                        {v.price_modifier ? (
                          <span className="block text-[10px] opacity-70">
                            {v.price_modifier > 0 ? "+" : ""}£{v.price_modifier.toFixed(2)}/m
                          </span>
                        ) : null}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs font-medium">Metres</Label>
                <div className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-border bg-background">
                  <button
                    onClick={() => setMetres((m) => Math.max(1, m - 1))}
                    className="p-2 hover:bg-muted rounded-l-full"
                    aria-label="Decrease metres"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <input
                    type="number"
                    value={metres}
                    onChange={(e) => setMetres(Math.max(1, parseInt(e.target.value) || 1))}
                    min={1}
                    className="w-14 bg-transparent text-center text-sm font-medium outline-none"
                  />
                  <button
                    onClick={() => setMetres((m) => m + 1)}
                    className="p-2 hover:bg-muted rounded-r-full"
                    aria-label="Increase metres"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              <div>
                <Label className="text-xs font-medium">Quantity</Label>
                <div className="mt-1.5 grid grid-cols-5 gap-1.5">
                  {[1, 2, 5, 10, 25].map((q) => (
                    <button
                      key={q}
                      onClick={() => setQuantity(q)}
                      className={cn(
                        "rounded-xl border py-1.5 text-sm font-medium transition-all",
                        quantity === q
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-border hover:border-primary/40",
                      )}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-2xl bg-muted/40 px-4 py-3 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Line total</span>
              <span className="text-base font-semibold tabular-nums">£{lineTotal.toFixed(2)}</span>
            </div>

            {gangsheetMissingConfig && (
              <div className="rounded-2xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200/60 dark:border-amber-900/40 p-3 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-800 dark:text-amber-200">
                  Gang sheet builder isn't configured. You can still add this to cart and our team
                  will follow up about artwork.
                </p>
              </div>
            )}

            {/* Primary action */}
            <Button
              className="w-full rounded-2xl bg-gradient-hero text-primary-foreground shadow-soft hover:opacity-90 gap-2"
              onClick={handleAddToCart}
            >
              <ShoppingCart className="h-4 w-4" /> Add to cart
            </Button>

            {/* Secondary: jump straight to builder for gang sheet products */}
            {product.is_gangsheet && !gangsheetMissingConfig && (
              <>
                <Button
                  variant="outline"
                  className="w-full rounded-2xl gap-2"
                  onClick={handleStartGangSheet}
                >
                  <Zap className="h-4 w-4" />
                  {builderStatus === "ready" && "Build gang sheet now"}
                  {builderStatus === "loading" && "Loading builder…"}
                  {builderStatus === "checking" && "Checking builder…"}
                  {builderStatus === "error" && "Builder unavailable — retry"}
                  {builderStatus === "disabled" && "Builder disabled"}
                  {builderStatus === "missing-shop" && "Builder not configured"}
                </Button>
                {builderStatus === "error" && builderError && (
                  <p className="text-xs text-destructive" role="alert">
                    {builderError}
                  </p>
                )}
              </>
            )}

            {product.allow_manual_upload && (
              <button
                onClick={handleStartJob}
                className="w-full text-xs text-muted-foreground hover:text-foreground inline-flex items-center justify-center gap-1.5 pt-1"
              >
                <Upload className="h-3 w-3" /> Or upload a finished gang sheet
              </button>
            )}

            {product.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-2 border-t border-border">
                {product.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
